/**
 * Portal v2 domains list (`/portal/domains`).
 *
 * View-only — no add CTA. SectionHeader + 3 derived KPI tiles + CardV2
 * div-grid table with verification StatPill + tinted pass-rate StatPill.
 * Empty state surfaces a friendly "partner hasn't added domains yet" line.
 *
 * Re-check DNS panel: inline expandable section below the domain table
 * that fires GET /portal/dns-check and renders per-record pass/fail.
 */
import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertCircle, CheckCircle2, Clock, Globe, RefreshCw, XCircle } from "lucide-react";
import { Skeleton, Button } from "@ddmarc/ui";
import { usePortalScope } from "@/lib/portal-scope-context";
import { portalApi, type PortalDomain, type PortalDnsCheck } from "@/lib/portal-api";
import { toast } from "@/hooks/use-toast";
import {
  CardV2,
  KpiTile,
  SectionHeader,
  StatPill,
} from "@/components/primitives";

const passRateTone = (pct: number): "good" | "warn" | "danger" =>
  pct >= 95 ? "good" : pct >= 80 ? "warn" : "danger";

const overallStatusTone = (status: PortalDnsCheck["overall_status"]): "good" | "warn" | "danger" =>
  status === "pass" ? "good" : status === "warning" ? "warn" : "danger";

const overallStatusLabel: Record<PortalDnsCheck["overall_status"], string> = {
  pass: "Pass",
  warning: "Warning",
  fail: "Fail",
};

interface DnsRecordRowProps {
  label: string;
  valid: boolean;
  record: string | null;
  errors: string[];
  warnings: string[];
  note?: string | null;
}

function DnsRecordRow({ label, valid, record, errors, warnings, note }: DnsRecordRowProps) {
  return (
    <div className="space-y-0.5 py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-foreground">{label}</span>
        {valid ? (
          <StatPill tone="good">
            <CheckCircle2 className="h-3 w-3" />
            Valid
          </StatPill>
        ) : (
          <StatPill tone="danger">
            <XCircle className="h-3 w-3" />
            Invalid
          </StatPill>
        )}
      </div>
      {record && (
        <p className="font-mono-data truncate text-[11px] text-muted-foreground">{record}</p>
      )}
      {errors.map((e, i) => (
        <p key={i} className="flex items-start gap-1 text-[11px] text-[var(--v2-danger)]">
          <XCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {e}
        </p>
      ))}
      {warnings.map((w, i) => (
        <p key={i} className="flex items-start gap-1 text-[11px] text-[var(--v2-warn)]">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {w}
        </p>
      ))}
      {note && (
        <p className="flex items-start gap-1 text-[11px] text-muted-foreground">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {note}
        </p>
      )}
    </div>
  );
}

function DnsCheckPanel() {
  const { actionableDomainId, selectedDomain, hasMultipleDomains } =
    usePortalScope();
  const [result, setResult] = useState<PortalDnsCheck | null>(null);

  // A DNS check probes ONE domain. Clear the previous result when the scope
  // changes so the panel never shows domain A's verdict under domain B's name.
  useEffect(() => {
    setResult(null);
  }, [actionableDomainId]);

  const { mutate: runCheck, isPending } = useMutation({
    mutationFn: () => portalApi.checkDns({ domain_id: actionableDomainId }),
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (err: Error) => {
      toast.error({ title: "DNS check failed", description: err.message });
    },
  });

  return (
    <CardV2
      title="DNS Health"
      subtitle={
        selectedDomain
          ? `Verify SPF, DKIM, and DMARC records for ${selectedDomain.domain}.`
          : hasMultipleDomains
            ? "Pick a domain above to check its SPF, DKIM, and DMARC records."
            : "Verify SPF, DKIM, and DMARC records for your domain."
      }
      action={
        <Button
          size="sm"
          variant="outline"
          onClick={() => runCheck()}
          disabled={isPending || (hasMultipleDomains && actionableDomainId === null)}
          className="h-7 gap-1.5 text-[12px]"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isPending ? "animate-spin" : ""}`} />
          {isPending ? "Checking…" : "Re-check DNS"}
        </Button>
      }
    >
      {result ? (
        <div className="space-y-0">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[12px] text-muted-foreground">
              {result.domain}
            </span>
            <StatPill tone={overallStatusTone(result.overall_status)}>
              {result.overall_status === "pass" ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              {overallStatusLabel[result.overall_status]}
            </StatPill>
          </div>
          <div className="divide-y divide-border">
            <DnsRecordRow
              label="SPF"
              valid={result.spf.valid}
              record={result.spf.record}
              errors={result.spf.errors}
              warnings={result.spf.warnings}
            />
            <DnsRecordRow
              label="DKIM"
              valid={result.dkim.valid}
              record={result.dkim.record}
              errors={result.dkim.errors}
              warnings={result.dkim.warnings}
              note={result.dkim.dkim_alignment_note}
            />
            <DnsRecordRow
              label="DMARC"
              valid={result.dmarc.valid}
              record={result.dmarc.record}
              errors={result.dmarc.errors}
              warnings={result.dmarc.warnings}
            />
          </div>
        </div>
      ) : (
        <p className="text-[12.5px] text-muted-foreground">
          Click "Re-check DNS" to validate your current DNS records.
        </p>
      )}
    </CardV2>
  );
}

export function PortalDomainsV2Page() {
  const { data, isLoading } = useQuery({
    queryKey: ["portal", "domains", "list"],
    queryFn: () => portalApi.listDomains({ size: 100 }),
  });

  const items: PortalDomain[] = data?.items ?? [];
  const total = items.length;
  const verifiedCount = items.filter((d) => d.is_verified).length;
  const verifiedPct = total ? (verifiedCount / total) * 100 : 0;
  const avgPass = total
    ? items.reduce((s, d) => s + d.pass_rate, 0) / total
    : 0;

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Domains
          </span>
        }
        subtitle="Monitored domains and their DMARC posture."
      />

      <div className="grid gap-3 md:grid-cols-3">
        <KpiTile
          label="Total domains"
          value={isLoading ? <Skeleton className="h-7 w-16" /> : total.toLocaleString()}
          hint="Monitored"
        />
        <KpiTile
          label="Verified"
          value={
            isLoading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              `${verifiedPct.toFixed(0)}%`
            )
          }
          hint={`${verifiedCount} of ${total}`}
        />
        <KpiTile
          label="Avg pass rate"
          value={
            isLoading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <span
                style={{
                  color: avgPass < 70 ? "var(--v2-warn)" : undefined,
                }}
              >
                {avgPass.toFixed(1)}%
              </span>
            )
          }
          hint="Across all domains"
        />
      </div>

      <CardV2 bodyClassName="p-0">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Globe className="h-10 w-10 text-[var(--v2-fg-faint)]" />
            <p className="text-[13px] font-medium text-foreground">
              No domains yet
            </p>
            <p className="text-[12.5px] text-muted-foreground">
              Your partner hasn't added domains to monitor yet.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.8fr_1fr] gap-3 px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
              <span>Domain</span>
              <span>Status</span>
              <span className="text-right">Pass rate</span>
              <span className="text-right">Reports</span>
              <span className="text-right">Last report</span>
            </div>
            {items.map((d) => (
              <div
                key={d.id}
                className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.8fr_1fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <Link
                    to="/portal/domains/$domainId"
                    params={{ domainId: String(d.id) }}
                    className="font-mono-data truncate text-foreground underline-offset-2 hover:underline"
                  >
                    {d.domain}
                  </Link>
                </span>
                <span>
                  {d.is_verified ? (
                    <StatPill tone="good">
                      <CheckCircle2 className="h-3 w-3" />
                      Verified
                    </StatPill>
                  ) : (
                    <StatPill tone="warn">
                      <Clock className="h-3 w-3" />
                      Pending
                    </StatPill>
                  )}
                </span>
                <span className="text-right">
                  <StatPill tone={passRateTone(d.pass_rate)}>
                    {d.pass_rate.toFixed(1)}%
                  </StatPill>
                </span>
                <span className="tnum text-right text-foreground">
                  {d.reports_count.toLocaleString()}
                </span>
                <span className="text-right text-muted-foreground">
                  {d.last_report_at
                    ? format(new Date(d.last_report_at), "MMM d, yyyy")
                    : "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardV2>

      <DnsCheckPanel />
    </div>
  );
}
